import {currentAttendanceReturn,safeAttendanceReturn} from '../lib/attendance-return.js';
// pwa/views/auth.js — email magic-link auth flow (ADR 20260527 v2.2).
// One surface = the sign-in flow. Screens ported from mockups/auth.html:
// boot/returning → welcome → email → check-inbox → success, plus confirm
// (magic-link landing, POST-only), not-setup, link-expired, lockout.

import { api, API_BASE } from '../lib/api.js';
import { $session, $authEmail } from '../lib/store.js';
import { icon } from '../lib/icons.js';
import { setHTML, scrollTop, esc, nameFromEmail } from '../lib/dom.js';
import { mountTrainerDashboard } from './trainer-dashboard.js';
import { offerPushPermissionIfEligible } from '../lib/push-permission.js';

let ROOT = null;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
let resendTimer = null;

// ── Shared chrome ──────────────────────────────────────────────
const header = () => `
  <header class="sticky top-0 z-30 bg-nc-ocean flex items-center gap-2.5 px-4 h-14">
    ${icon('waves', 'w-5 h-5 text-nc-sand-yellow')}
    <span class="font-bold text-lg text-white tracking-tight">TAF Hub</span>
  </header>`;

function screen(inner) {
  // A newer dispatch may have replaced #app-main (its replaceChildren detaches
  // our ROOT) while this auth render was awaiting /me — writing innerHTML to a
  // null/detached node throws. Abort the stale render instead.
  if (!ROOT || !ROOT.isConnected) return;
  setHTML(ROOT, `${header()}<section class="auth-screen min-h-[calc(100dvh-3.5rem)] flex flex-col items-center justify-center pb-10">${inner}</section>`);
  scrollTop();
}

const card = (inner) => `<div class="w-full max-w-sm mx-auto px-6 pt-12 pb-8">${inner}</div>`;
const primaryBtn = (id, label, ic = 'send', attrs = '') =>
  `<button id="${id}" ${attrs} class="btn-primary w-full rounded-xl font-extrabold text-base py-[1.1rem] flex items-center justify-center gap-2.5 min-h-[56px]">${icon(ic, 'w-5 h-5')}${label}</button>`;
const inlineError = (msg) => msg
  ? `<p id="auth-error" role="alert" class="text-nc-lifesaver text-sm font-semibold mt-3 text-center">${esc(msg)}</p>` : '';

// ── Public entry ───────────────────────────────────────────────
export function mountAuth(root, { screen: s = 'boot', token = '', hint = '' } = {}) {
  ROOT = root;
  if (s === 'confirm') return renderConfirm(token);
  if (s === 'oauth') return renderOAuthResult(hint);
  return renderBoot();
}

// ── OAuth result landing (the Worker callback bounced us back with ?auth=…) ──
// Success carries no hint — the callback redirects to "/" and the normal boot
// silent-sign-in resolves the freshly-set cookie. Only failures land here.
function renderOAuthResult(hint) {
  // Strip the transient hint while retaining the strictly allowlisted History
  // destination for Back to sign in, switching account and email fallback.
  const returnTo = currentAttendanceReturn();
  try { history.replaceState({}, '', returnTo || '/'); } catch { /* noop */ }
  if (hint === 'not_setup') return renderNotSetup('');
  if (hint === 'cancelled') return renderWelcome(); // user backed out of consent
  // 'error' | 'oauth_unavailable' | anything else → recoverable error screen.
  return renderOAuthError();
}

const GOOGLE_G = `<svg class="w-5 h-5" viewBox="0 0 18 18" aria-hidden="true"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92a8.78 8.78 0 0 0 2.68-6.62z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/></svg>`;

// Top-level navigation to the Worker's OAuth start route (NOT a fetch — the
// device cookie is set on the callback's redirect response).
function startGoogle() { window.location.assign(`${API_BASE}/api/auth/oauth/google/start${currentAttendanceReturn()?`?return_to=${encodeURIComponent(currentAttendanceReturn())}`:''}`); }

// ── Boot: returning-device silent sign-in, else welcome ────────
//
// Cache the resolved session in $session and treat it as authoritative for
// the lifetime of the page. SPA navigation back to / shouldn't re-fetch /me
// — that's what produces the "Welcome back" spinner flash the user reported
// (2026-05-29). Cold boot (no cached session) keeps the silent-sign-in path
// + spinner so the user gets feedback while we resolve the device cookie.
// The HttpOnly cookie is still the source of truth; the in-memory cache is
// purely a render optimisation. Visibility-change re-validation would be a
// nice-to-have follow-up but isn't required at the 45-user scale.
async function renderBoot() {
  const cached = $session.get();
  if (cached) {
    return renderAuthed(cached);
  }
  renderReturning();
  try {
    const me = await api.get('/api/auth/me');
    $session.set(me);
    return await renderAuthed(me);
  } catch {
    renderWelcome();
  }
}

// ── Welcome ────────────────────────────────────────────────────
function renderWelcome() {
  screen(card(`
    <div class="text-center mb-10">
      <div class="inline-flex items-center justify-center rounded-2xl bg-nc-ocean mb-6 shadow-overlay px-7 py-5"><img src="/email/ncslsc-crest-white@2x.png" alt="North Cottesloe SLSC" style="width:150px;height:auto;display:block" width="380" height="170"></div>
      <h1 class="text-foreground font-bold text-3xl leading-tight mb-3">TAF Hub</h1>
      <p class="text-muted-foreground text-base leading-relaxed">Trainer scheduling for North Cottesloe SLSC. Sign up for sessions, see who's teaching, manage your slots.</p>
    </div>
    <button id="go-google" class="w-full rounded-xl font-bold text-base py-[1.1rem] flex items-center justify-center gap-3 min-h-[56px] bg-white border border-[#dadce0] text-[#3c4043] shadow-card hover:bg-[#f8f9fa]">${GOOGLE_G}Continue with Google</button>
    <div class="flex items-center gap-3 my-4" aria-hidden="true">
      <span class="h-px flex-1 bg-border"></span>
      <span class="text-xs font-semibold text-muted-foreground uppercase tracking-wide">or</span>
      <span class="h-px flex-1 bg-border"></span>
    </div>
    ${primaryBtn('go-email', 'Sign in with email', 'mail')}
    <div class="mt-6 flex items-center justify-center gap-2  bg-card rounded-xl px-4 py-4 shadow-card">
      ${icon('shield-check', 'w-4 h-4 text-nc-surf-blue flex-shrink-0')}
      <p class="text-sm text-muted-foreground leading-relaxed text-center">No password needed. We send a one-tap sign-in link to your email.</p>
    </div>
    <p class="text-center mt-5">
      <a href="/help/starter" target="_blank" rel="noopener" class="inline-flex items-center justify-center gap-1.5 min-h-[44px] text-sm font-semibold text-nc-surf-blue hover:underline">${icon('book-open', 'w-4 h-4')}How it works</a>
    </p>`));
  byId('go-google').onclick = startGoogle;
  byId('go-email').onclick = () => renderEmail();
}

// ── Email entry ────────────────────────────────────────────────
function renderEmail(prefill = '', error = '') {
  screen(card(`
    <button id="back" class="flex items-center gap-1.5 text-muted-foreground text-sm font-semibold mb-8 hover:text-foreground min-h-[44px]">${icon('arrow-left', 'w-4 h-4')}Back</button>
    <div class="text-center mb-8">
      <div class="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-state-available mb-5">${icon('mail', 'w-6 h-6 text-nc-surf-blue')}</div>
      <h1 class="text-foreground font-bold text-2xl leading-tight mb-2">Sign in to TAF Hub</h1>
      <p class="text-muted-foreground text-base leading-relaxed">Enter your email and we'll send you a one-tap sign-in link. No passwords.</p>
    </div>
    <div class="mb-6">
      <label for="email" class="block text-foreground font-semibold text-sm mb-2 text-center">Email address</label>
      <input type="email" id="email" inputmode="email" autocomplete="email" value="${esc(prefill)}" placeholder="you@example.com" class="email-input w-full px-5 py-4 min-h-[56px]" aria-describedby="email-hint">
      <p id="email-hint" class="text-muted-foreground text-xs mt-2 text-center">Use the email the club has on file.</p>
    </div>
    <div id="turnstile" class="mb-4" style="min-height:65px;"></div>
    ${primaryBtn('send', 'Send me a sign-in link', 'send', 'disabled aria-disabled="true"')}
    ${inlineError(error)}`));

  const input = byId('email');
  const send = byId('send');
  // Send is gated on BOTH a valid email AND Turnstile completing (if configured).
  // Turnstile's callback re-enables the button; expired/error callbacks disable it again.
  const hasTurnstile = !!(window.__TURNSTILE_SITE_KEY__);
  const sync = () => {
    const emailOk = EMAIL_RE.test(input.value.trim());
    const turnstileOk = !hasTurnstile || !!turnstileToken();
    const ok = emailOk && turnstileOk;
    send.disabled = !ok; send.setAttribute('aria-disabled', String(!ok));
  };
  input.oninput = sync;
  input.onkeydown = (e) => { if (e.key === 'Enter' && !send.disabled) sendLink(); };
  byId('back').onclick = () => renderWelcome();
  send.onclick = () => sendLink();
  // Start disabled; Turnstile callback re-enables once challenge passes.
  send.disabled = true; send.setAttribute('aria-disabled', 'true');
  // Render widget — overrides the "disabled until email valid" state via callback.
  // If no Turnstile key, fall back to email-only gate (sync() below handles it).
  renderTurnstileWidget();
  if (!hasTurnstile) sync(); // no Turnstile → enable based on email only
  input.focus();
}

// Explicit Turnstile widget ID — avoids implicit auto-render timing races in the SPA.
let _turnstileWidgetId = null;

/**
 * Render the Turnstile widget explicitly into #turnstile after the email screen mounts.
 * Uses polling to wait for window.turnstile (async script) rather than the onload
 * callback, which fires before the email screen exists and triggers error 600010.
 * The #turnstile div has NO class="cf-turnstile" so Turnstile never implicit-renders it.
 */
function renderTurnstileWidget() {
  _turnstileWidgetId = null;
  const sitekey = window.__TURNSTILE_SITE_KEY__ || '';
  if (!sitekey) return; // key not configured → skip (no widget, Send enabled by email-only gate)

  const enableSend = (on) => {
    const send = ROOT ? ROOT.querySelector('#send') : null;
    if (send) { send.disabled = !on; send.setAttribute('aria-disabled', String(!on)); }
  };

  const doRender = () => {
    const el = ROOT ? ROOT.querySelector('#turnstile') : null;
    if (!el || !el.isConnected || !window.turnstile) return;
    if (_turnstileWidgetId !== null) {
      try { window.turnstile.remove(_turnstileWidgetId); } catch { /* noop */ }
      _turnstileWidgetId = null;
    }
    _turnstileWidgetId = window.turnstile.render(el, {
      sitekey,
      callback: () => enableSend(true),
      'expired-callback': () => enableSend(false),
      'error-callback': () => enableSend(false),
    });
  };

  if (window.turnstile) {
    doRender();
  } else {
    // Poll until the async Turnstile script has loaded — abandon after 15s.
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (window.turnstile) { clearInterval(interval); doRender(); }
      else if (attempts > 150) clearInterval(interval); // 150 × 100ms = 15s
    }, 100);
  }
}

function turnstileToken() {
  if (_turnstileWidgetId !== null && window.turnstile) {
    try { return window.turnstile.getResponse(_turnstileWidgetId) || ''; } catch { /* noop */ }
  }
  // Fallback: read from the hidden input (implicit render / no widget id yet).
  const el = ROOT ? ROOT.querySelector('[name="cf-turnstile-response"]') : null;
  return el ? el.value : '';
}

async function sendLink() {
  const email = byId('email').value.trim();
  if (!EMAIL_RE.test(email)) return;
  const send = byId('send');
  send.disabled = true; send.setAttribute('aria-disabled', 'true');
  $authEmail.set(email);
  try {
    const res = await api.post('/api/auth/request-link', { email, turnstileToken: turnstileToken(),return_to:currentAttendanceReturn() });
    if (res.status === 'not_setup') return renderNotSetup(email);
    renderCheckInbox(email);
  } catch (err) {
    if (err.status === 429) return renderLockout();
    renderEmail(email, err.message || 'Could not send the link. Try again.');
  }
}

// ── Check inbox (+ 30s resend cooldown) ────────────────────────
function renderCheckInbox(email) {
  screen(card(`
    <div class="flex justify-center mb-6"><div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-state-available-wash">${icon('inbox', 'w-10 h-10 text-nc-surf-blue')}</div></div>
    <h1 class="text-foreground font-bold text-2xl leading-tight text-center mb-3">Check your inbox</h1>
    <p class="text-muted-foreground text-base text-center mb-1">We sent a sign-in link to</p>
    <p class="text-foreground font-bold text-base text-center mb-2">${esc(email)}</p>
    <p class="text-muted-foreground text-sm text-center mb-8 max-w-xs mx-auto">Tap the link in that email to sign in. It's valid for 90 days and single-use.</p>
    <button id="resend" disabled aria-disabled="true" class="btn-ghost w-full rounded-xl font-semibold text-base py-[1.1rem] flex items-center justify-center gap-2.5 min-h-[56px] mb-4">${icon('refresh-cw', 'w-4 h-4')}<span id="resend-label" role="timer" aria-live="off">Resend in 30s</span></button>
    <span id="resend-announce" class="sr-only" aria-live="polite"></span>
    <div class="text-center mt-2"><button id="wrong-email" class="link-surf-blue font-semibold text-sm underline min-h-[44px] inline-flex items-center">Wrong email? Change it</button></div>`));
  byId('wrong-email').onclick = () => renderEmail(email);
  byId('resend').onclick = () => { resendCooldown(); api.post('/api/auth/request-link', { email, turnstileToken: turnstileToken(),return_to:currentAttendanceReturn() }).catch(() => {}); };
  resendCooldown();
}

function resendCooldown(seconds = 30) {
  const btn = byId('resend'); const label = byId('resend-label');
  if (!btn) return;
  if (resendTimer) clearInterval(resendTimer);
  let left = seconds;
  btn.disabled = true; btn.setAttribute('aria-disabled', 'true');
  const tick = () => {
    label.textContent = `Resend in ${left}s`;
    if (left <= 0) {
      clearInterval(resendTimer); resendTimer = null;
      btn.disabled = false; btn.setAttribute('aria-disabled', 'false');
      label.textContent = 'Resend email';
      const a = byId('resend-announce'); if (a) a.textContent = 'You can resend the sign-in email now.';
    }
    left -= 1;
  };
  tick(); resendTimer = setInterval(tick, 1000);
}

// ── Confirm (magic-link landing — token consumed on POST only) ──
function renderConfirm(token) {
  screen(card(`
    <div class="flex justify-center mb-6"><div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-state-available-wash">${icon('shield-check', 'w-10 h-10 text-nc-surf-blue')}</div></div>
    <h1 class="text-foreground font-bold text-2xl leading-tight text-center mb-3">Finish signing in</h1>
    <p class="text-muted-foreground text-base text-center mb-8 max-w-xs mx-auto">Tap below to complete your sign-in on this device.</p>
    ${primaryBtn('confirm', 'Sign me in', 'check', token ? '' : 'disabled aria-disabled="true"')}
    ${token ? '' : inlineError('This link is missing its sign-in code. Request a new one.')}`));
  if (token) byId('confirm').onclick = () => doConfirm(token);
}

async function doConfirm(token) {
  const btn = byId('confirm');
  btn.disabled = true; btn.setAttribute('aria-disabled', 'true');
  btn.innerHTML = `${icon('loader', 'w-5 h-5 spinner')}Signing in…`;
  try {
    const confirmed=await api.post('/api/auth/confirm', { token });
    const me = await api.get('/api/auth/me');
    $session.set(me);
    if(safeAttendanceReturn(confirmed.return_to)){location.assign(safeAttendanceReturn(confirmed.return_to));return;}
    history.replaceState({}, '', '/'); // drop the token from the URL
    renderSuccess(me);
  } catch (err) {
    if (err.status === 410) return renderLinkExpired();
    renderConfirm(''); // fall back to the missing/used state
  }
}

// ── Success ────────────────────────────────────────────────────
function renderSuccess(me) {
  const name = me?.first_name || nameFromEmail(me?.email);
  screen(`<div class="w-full max-w-sm mx-auto px-6 py-8 text-center">
    <div class="inline-flex items-center justify-center w-24 h-24 rounded-full bg-nc-reef mb-6 success-icon" role="img" aria-label="Signed in">${icon('check', 'w-12 h-12 text-foreground')}</div>
    <h1 class="text-foreground font-bold text-3xl leading-tight mb-2">You're in</h1>
    <p class="text-foreground font-semibold text-xl mb-3">Welcome, ${esc(name)}</p>
    <p class="text-muted-foreground text-base mb-8 max-w-xs mx-auto">We'll remember this device so you can skip sign-in next time.</p>
    ${primaryBtn('go-sessions', 'Go to Sessions', 'layout-grid')}</div>`);
  byId('go-sessions').onclick = () => {
    // ADR 20260601 trigger 3: offer push on the onboarding success screen.
    // "Get notified if a role opens for you?" soft opt-in. Must be inside the
    // click handler to satisfy the user-gesture requirement (iOS + Chrome policy).
    offerPushPermissionIfEligible();
    renderAuthed(me);
  };
}

// ── Returning (device-bound silent sign-in spinner) ────────────
function renderReturning() {
  screen(`<div class="w-full max-w-sm mx-auto px-6 py-8 text-center">
    <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-state-available mb-8">${icon('loader', 'w-8 h-8 text-nc-surf-blue spinner')}</div>
    <h1 class="text-foreground font-bold text-2xl leading-tight mb-2">Welcome back</h1>
    <p class="text-muted-foreground text-base max-w-xs mx-auto">Signing you in on this device…</p></div>`);
}

// ── Not set up (admin-invite) ──────────────────────────────────
// `email` is the address the user tried (magic-link path). OAuth lands here with
// an empty email — we never carry the Google address back in the URL — so the
// copy adapts to the generic "that account" wording.
function renderNotSetup(email) {
  const who = email
    ? `We don't have <strong class="text-foreground">${esc(email)}</strong> on file.`
    : `We couldn't match that account to anyone on file.`;
  // Build the "Request access" link — pass email as a query param so the
  // onboarding wizard can pre-fill the field (esc() guards the URL value).
  const onboardingHref = email
    ? `/onboarding?email=${encodeURIComponent(email)}`
    : '/onboarding';
  screen(card(`<div class="text-center">
    <div class="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-state-available mb-6">${icon('user-search', 'w-8 h-8 text-nc-surf-blue')}</div>
    <h1 class="text-foreground font-bold text-2xl leading-tight mb-2">You're not set up yet</h1>
    <p class="text-muted-foreground text-base mb-6 max-w-sm mx-auto">${who} Already a member? Ask the Head Trainer to add you. New to the club? You can request access directly.</p>
    <div class="space-y-3 text-left">
      <a href="${onboardingHref}" data-nav class="btn-primary w-full rounded-xl font-extrabold text-base py-[1.1rem] flex items-center justify-center gap-2.5 min-h-[56px] no-underline">${icon('user-plus', 'w-5 h-5')}Request access</a>
      <a href="mailto:education@ncslsc.com?subject=Please%20add%20me%20to%20the%20TAF%20Hub" class="btn-ghost w-full rounded-xl font-semibold text-base py-[1.1rem] flex items-center justify-center gap-2.5 min-h-[56px] no-underline">${icon('mail', 'w-5 h-5')}Email the Head Trainer</a>
      <button id="retry" class="btn-ghost w-full rounded-xl font-semibold text-base py-[1.1rem] flex items-center justify-center gap-2.5 min-h-[56px]">${icon('arrow-left', 'w-4 h-4')}Back to sign in</button>
    </div></div>`));
  byId('retry').onclick = () => renderWelcome();
}

// ── OAuth error (provider/exchange/verify failed, or OAuth not configured) ──
function renderOAuthError() {
  screen(card(`<div class="text-center">
    <div class="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-danger-wash mb-6">${icon('alert-triangle', 'w-8 h-8 text-nc-lifesaver')}</div>
    <h1 class="text-foreground font-bold text-2xl leading-tight mb-2">Sign-in didn't complete</h1>
    <p class="text-muted-foreground text-base mb-8 max-w-xs mx-auto">Something interrupted that sign-in. You can try again, or use a one-tap email link instead.</p>
    <div class="space-y-3">
      <button id="retry-google" class="w-full rounded-xl font-bold text-base py-[1.1rem] flex items-center justify-center gap-3 min-h-[56px] bg-white border border-[#dadce0] text-[#3c4043] shadow-card hover:bg-[#f8f9fa]">${GOOGLE_G}Continue with Google</button>
      <button id="use-email" class="btn-ghost w-full rounded-xl font-semibold text-base py-[1.1rem] flex items-center justify-center gap-2.5 min-h-[56px]">${icon('mail', 'w-5 h-5')}Use an email link instead</button>
    </div></div>`));
  byId('retry-google').onclick = startGoogle;
  byId('use-email').onclick = () => renderEmail();
}

// ── Link expired ───────────────────────────────────────────────
function renderLinkExpired() {
  screen(card(`<div class="text-center">
    <div class="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-danger-wash mb-6">${icon('clock', 'w-8 h-8 text-nc-lifesaver')}</div>
    <h1 class="text-foreground font-bold text-2xl leading-tight mb-2">This link has expired</h1>
    <p class="text-muted-foreground text-base mb-8 max-w-xs mx-auto">This sign-in link has already been used. Links are single-use — send a fresh one and you'll be in straight away.</p>
    ${primaryBtn('new-link', 'Send a new link', 'send')}</div>`));
  byId('new-link').onclick = () => renderEmail($authEmail.get());
}

// ── Lockout ────────────────────────────────────────────────────
function renderLockout() {
  screen(card(`<div class="text-center">
    <div class="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-danger-wash mb-6">${icon('lock', 'w-8 h-8 text-nc-lifesaver')}</div>
    <h1 class="text-foreground font-bold text-2xl leading-tight mb-2">Sign in paused</h1>
    <p class="text-muted-foreground text-base mb-8 max-w-sm mx-auto">We've had a few too many link requests for this account in a short time. Wait a little and try again. Locked out unexpectedly? Email the Head Trainer.</p>
    <div class="space-y-3">
      ${primaryBtn('home', 'Back to start', 'home')}
      <a href="mailto:education@ncslsc.com" class="btn-ghost w-full rounded-xl font-semibold text-base py-[1.1rem] flex items-center justify-center gap-2.5 min-h-[56px] no-underline">${icon('mail', 'w-5 h-5')}Contact Head Trainer</a>
    </div></div>`));
  byId('home').onclick = () => renderWelcome();
}

// ── Authed → hand off to the `/` landing (trainer dashboard, spec 004) ──
// The sessions board moved to /signup (app.js dispatch); `/` is the dashboard.
function renderAuthed(me) {
  return mountTrainerDashboard(ROOT, me);
}

function byId(id) { return ROOT.querySelector('#' + id); }
